import { AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import PaginatedResponse from '@/types/paginatedResponse';
import PersonPersonRelationship, {
  PersonPersonRelationshipType
} from '@/types/personPersonRelationship';
import { QueryParams } from '@/types/query';
import ajax from '@/utils/ajax';

export const personPersonRelationshipRepository = {
  BASE_PATH: '/api/dugout/person_person_relationship/',

  async getPersonPersonRelationship({
    host,
    personPersonRelationshipId
  }: {
    host: string;
    personPersonRelationshipId: string;
  }): Promise<PersonPersonRelationship> {
    const path = `${this.BASE_PATH}${personPersonRelationshipId}/`;
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data;
  },

  async getPersonPersonRelationships({
    host,
    params
  }: {
    host: string;
    params: QueryParams;
  }): Promise<PaginatedResponse<PersonPersonRelationship[]>> {
    const path = this.BASE_PATH;
    const response = await ajax.get({
      url: `${host}${path}`,
      params
    });

    return response.data;
  },

  async createPersonPersonRelationship({
    host,
    params
  }: {
    host: string;
    params: Partial<PersonPersonRelationship>;
  }): Promise<PersonPersonRelationship> {
    const path = this.BASE_PATH;
    const response = await ajax.post<
      Partial<PersonPersonRelationship>,
      AxiosResponse<PersonPersonRelationship>
    >({
      url: `${host}${path}`,
      data: params
    });

    return response.data;
  },

  async updatePersonPersonRelationship({
    host,
    personPersonRelationshipId,
    params
  }: {
    host: string;
    personPersonRelationshipId: string;
    params: Partial<PersonPersonRelationship>;
  }): Promise<PersonPersonRelationship> {
    const path = `${this.BASE_PATH}${personPersonRelationshipId}/`;
    const response = await ajax.patch<
      Partial<PersonPersonRelationship>,
      AxiosResponse<PersonPersonRelationship>
    >({
      url: `${host}${path}`,
      data: params
    });

    return response.data;
  },

  async deletePersonPersonRelationship({
    host,
    personPersonRelationshipId
  }: {
    host: string;
    personPersonRelationshipId: string;
  }): Promise<void> {
    const path = `${this.BASE_PATH}${personPersonRelationshipId}/`;
    await ajax.delete({
      url: `${host}${path}`,
      data: {}
    });
  },

  async getPersonPersonRelationshipTypes({
    host,
    params
  }: {
    host: string;
    params: QueryParams;
  }): Promise<PaginatedResponse<PersonPersonRelationshipType[]>> {
    const path = `${this.BASE_PATH}list_types/`;
    const response = await ajax.get({
      url: `${host}${path}`,
      params
    });

    return response.data;
  }
};

const PersonPersonRelationshipRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getPersonPersonRelationship(
      personPersonRelationshipId: string
    ): Promise<PersonPersonRelationship> {
      return personPersonRelationshipRepository.getPersonPersonRelationship({
        host,
        personPersonRelationshipId
      });
    },

    async getPersonPersonRelationships(
      params: QueryParams
    ): Promise<PaginatedResponse<PersonPersonRelationship[]>> {
      return personPersonRelationshipRepository.getPersonPersonRelationships({
        host,
        params
      });
    },

    async createPersonPersonRelationship(
      params: Partial<PersonPersonRelationship>
    ): Promise<PersonPersonRelationship> {
      return personPersonRelationshipRepository.createPersonPersonRelationship({
        host,
        params
      });
    },

    async updatePersonPersonRelationship(
      personPersonRelationshipId: string,
      params: Partial<PersonPersonRelationship>
    ): Promise<PersonPersonRelationship> {
      return personPersonRelationshipRepository.updatePersonPersonRelationship({
        host,
        personPersonRelationshipId,
        params
      });
    },

    async deletePersonPersonRelationship(personPersonRelationshipId: string): Promise<void> {
      return personPersonRelationshipRepository.deletePersonPersonRelationship({
        host,
        personPersonRelationshipId
      });
    },

    async getPersonPersonRelationshipTypes(
      params: QueryParams
    ): Promise<PaginatedResponse<PersonPersonRelationshipType[]>> {
      return personPersonRelationshipRepository.getPersonPersonRelationshipTypes({ host, params });
    }
  };
};

export default PersonPersonRelationshipRepository;
